import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronDown } from "lucide-react";
import { getService, SERVICES } from "@/data/services";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);

  if (!service) {
    return (
      <main className="pt-48 pb-40 text-center" data-testid="service-not-found">
        <p className="font-editorial italic text-4xl text-slate-400">That service has moved on.</p>
        <Link to="/services" data-testid="service-back-link" className="mt-6 inline-flex items-center gap-2 text-sm text-brand">
          <ArrowLeft size={15} /> Back to all services
        </Link>
      </main>
    );
  }

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const Icon = service.icon;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <main data-testid="service-detail-page" className="pt-40 pb-32">
      <Seo
        title={`${service.title} — Samanvay HR`}
        description={service.excerpt}
        jsonLd={faqJsonLd}
      />
      <div className="max-w-7xl mx-auto px-6">
        <Link to="/services" data-testid="service-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand transition-colors duration-200">
          <ArrowLeft size={15} /> All services
        </Link>

        <div className="mt-10 max-w-4xl">
          <SectionTag>Services</SectionTag>
          <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-4xl sm:text-5xl lg:text-6xl text-slate-900" data-testid="service-title">
            <MaskLine delay={0.15}>{service.title}</MaskLine>
          </h1>
          <FadeUp delay={0.35}>
            <p className="mt-5 font-editorial italic text-2xl sm:text-3xl text-brand leading-snug" data-testid="service-tagline">
              {service.tagline}
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <FadeUp>
              <div className="space-y-5" data-testid="service-overview">
                {service.overview.map((p, i) => (
                  <p key={i} className="text-base sm:text-lg text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.1} className="mt-14">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">What&rsquo;s included</h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="service-includes">
                {service.includes.map((inc) => (
                  <div key={inc.title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`include-${inc.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <p className="flex items-start gap-3 font-display font-semibold text-slate-900">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-50 text-brand border border-orange-200/50">
                        <Check size={13} />
                      </span>
                      {inc.title}
                    </p>
                    <p className="mt-2 pl-9 text-sm text-slate-600 leading-relaxed">{inc.body}</p>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.15} className="mt-14">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Common questions</h2>
              <div className="mt-6 space-y-3" data-testid="service-faqs">
                {service.faqs.map((f, i) => (
                  <details key={i} className="group rounded-2xl border border-slate-200 bg-white px-6 py-5" data-testid={`faq-${i}`}>
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display font-semibold text-slate-900">
                      {f.q}
                      <ChevronDown size={17} className="shrink-0 text-slate-400 transition-transform duration-300 group-open:rotate-180" />
                    </summary>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </FadeUp>
          </div>

          <div className="lg:col-span-4">
            <FadeUp delay={0.2}>
              <div className="lg:sticky lg:top-28 space-y-5">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="service-sidecard">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 text-brand flex items-center justify-center border border-orange-200/50">
                    <Icon size={22} />
                  </div>
                  <h2 className="mt-6 font-display text-xl font-semibold text-slate-900">Who it&rsquo;s for</h2>
                  <ul className="mt-4 space-y-3">
                    {service.whoFor.map((w) => (
                      <li key={w} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                        {w}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid="service-cta-btn"
                    className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
                  >
                    Talk to us about this
                    <ArrowUpRightIcon />
                  </Link>
                </div>

                <Link
                  to={`/services/${next.slug}`}
                  data-testid="service-next-link"
                  className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-6 transition-colors duration-300 hover:border-brand/50"
                >
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400">Next service</p>
                    <p className="mt-1 font-display font-semibold text-slate-900">{next.title}</p>
                  </div>
                  <ArrowRight size={18} className="text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </main>
  );
}

const ArrowUpRightIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
    <path d="M7 7h10v10" /><path d="M7 17 17 7" />
  </svg>
);
